import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { clearModals, popModal, pushModal, replaceTopModal, resolveBack, topModal, type ModalEntry } from '../../../lib/modal-stack';

/**
 * A shared host for Symphony's entity drill-ins (task / goal / agent-skills / file details). These used
 * to open a right-docked side panel; they are all centered modals now (the Symphony DS "Modal /
 * Drawer"). A drill-in renders a self-contained <Modal> — it owns its own scrim, close and Escape
 * stack, reads `closeModal` from here, and deliberately does NOT touch the rail, so opening a detail
 * never collapses the left navigation.
 *
 * **It is a STACK, and it used to be a single slot.** Opening a drill-in from inside another one
 * REPLACED it, which is how the goal panel lost its reader: open a goal, click a step, and the task
 * modal took the slot — the only way back to the goal was to dismiss everything and find it again in
 * the grid. Reported in exactly those words ("I have no way to go back to the goal since the task
 * dialog opens and replaces the goals dialog").
 *
 * Three properties, each of them a requirement rather than an implementation detail:
 *
 * - **Only the TOP entry renders.** Two live `<Modal>`s means two scrims and two Escape handlers.
 *   The entries beneath are unmounted, which is safe because a drill-in reads its entity from the
 *   React Query cache — coming back re-selects the live copy rather than restoring a stale snapshot.
 * - **The route IN decides the route OUT.** `back` exists only when there IS something underneath, so
 *   a task opened from the Tasks list (an empty stack) grows no back affordance pointing at a goal it
 *   never came from. Nothing has to remember to suppress it.
 * - **Closing means closing.** `closeModal` empties the whole stack. Turning the close control into a
 *   back control strands a reader who wanted out of both — the opposite complaint, one level down.
 */
interface TaskSidePanelValue {
  /** The rendered modal node, or null. Only the top of the stack. */
  modal: ReactNode | null;
  /** The open drill-in's owning id, or null. */
  openTaskId: string | null;
  /** Push a drill-in. `backLabel` names what the reader would return to, and is what makes the back
   *  control legible — "Back to Launch the book club", not a bare arrow. */
  openModal: (node: ReactNode, id?: string, options?: { backLabel?: string }) => void;
  /** Swap the open drill-in for another IN PLACE (no push, no close/reopen) — the thread dialog's
   *  Previous / Next. Same component type → React keeps the Modal mounted and only its props change. */
  replaceModal: (node: ReactNode, id?: string) => void;
  /** Dismiss EVERY open drill-in. */
  closeModal: () => void;
  /** Return to the drill-in underneath, or undefined when this is the only one open. */
  back?: () => void;
  /** The name of what `back` returns to. Undefined whenever `back` is. */
  backLabel?: string;
}

const Context = createContext<TaskSidePanelValue | null>(null);

export function TaskSidePanelProvider({ children }: { children: ReactNode }) {
  // Every rule about this stack lives in `lib/modal-stack.ts`, import-free and unit-tested; this is the
  // React binding for it and decides nothing of its own.
  const [stack, setStack] = useState<ModalEntry<ReactNode>[]>([]);
  const openModal = useCallback(
    (node: ReactNode, id?: string, options?: { backLabel?: string }) =>
      setStack((current) => pushModal(current, { node, id: id ?? null, backLabel: options?.backLabel })),
    [],
  );
  const replaceModal = useCallback(
    (node: ReactNode, id?: string) => setStack((current) => replaceTopModal(current, { node, id: id ?? null })),
    [],
  );
  const closeModal = useCallback(() => setStack(clearModals<ReactNode>()), []);
  const goBack = useCallback(() => setStack((current) => popModal(current)), []);
  const value = useMemo(() => {
    const top = topModal(stack);
    const back = resolveBack(stack);
    return {
      modal: top?.node ?? null,
      openTaskId: top?.id ?? null,
      openModal,
      replaceModal,
      closeModal,
      ...(back ? { back: goBack, backLabel: back.label } : {}),
    };
  }, [stack, openModal, replaceModal, closeModal, goBack]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

// Tolerates a missing provider (no-op) so a stray mount never throws.
export function useTaskSidePanel(): TaskSidePanelValue {
  return useContext(Context) ?? { modal: null, openTaskId: null, openModal: () => {}, replaceModal: () => {}, closeModal: () => {} };
}
